import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, Gift, ChevronDown, Copy, Loader2, BedDouble, Baby as BabyIcon, Car, Video, Hash, ArrowLeft, Edit, CalendarPlus, Lock, FileDown, Info } from "lucide-react";
import { toast } from "sonner";
import { getInvitationBySlug, getInvitationByPreviewToken } from "@/lib/api";
import { getTheme } from "@/lib/themes";
import { buildStyleVars } from "@/lib/fonts";
import { resolveImageUrl } from "@/components/ImageUpload";
import { resolveBlockOrder } from "@/lib/blocks";
import { formatTime12h, hexLuminance, hexToRgba } from "@/lib/utils";
import { loadGoogleFont } from "@/lib/googleFonts";
import { withBigEmojis } from "@/lib/emoji";
import { useAuth } from "@/lib/auth";
import { useThemeVars } from "@/lib/useThemeVars";
import { exportElementAsPdf } from "@/lib/exportPdf";
import Countdown from "@/components/Countdown";
import Dresscode from "@/components/Dresscode";
import RSVPForm from "@/components/RSVPForm";
import MusicPlayer from "@/components/MusicPlayer";
import Itinerary from "@/components/Itinerary";
import Padrinos from "@/components/Padrinos";
import Guestbook from "@/components/Guestbook";
import PlaylistCollaborative from "@/components/PlaylistCollaborative";

const monthNames = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
const dayNames = ["DOM", "LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB"];

function formatDate(dateStr) {
  if (!dateStr) return { day: "--", month: "---", year: "----", weekday: "---" };
  const d = new Date(`${dateStr}T00:00:00`);
  return {
    day: String(d.getDate()).padStart(2, "0"),
    month: monthNames[d.getMonth()],
    year: d.getFullYear(),
    weekday: dayNames[d.getDay()],
  };
}

// Returns a usable href if the gift-registry value is a URL; null if it's just a message.
function giftRegistryHref(v) {
  const s = (v || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (!/\s/.test(s) && /^([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(s)) return `https://${s}`;
  return null;
}

// Wrapper that keeps a clean section — background is now global (via GlobalBackground component)
function BlockShell({ className = "", children, testid }) {
  return <section className={className} data-testid={testid}>{children}</section>;
}

// Builds the colored blur ("difuminado") behind a hero text as a textShadow.
// level: "none" | "soft" | "medium" | "strong". baseBlur/baseAlpha define the "medium" look.
const HERO_GLOW_FACTOR = { soft: 0.5, medium: 1, strong: 1.7 };
const HERO_GLOW_ALPHA = { soft: 0.7, medium: 1, strong: 1.3 };
function heroGlowShadow(level, color, offY, baseBlur, baseAlpha) {
  if (!level || level === "none") return "none";
  const blur = Math.round(baseBlur * (HERO_GLOW_FACTOR[level] ?? 1));
  const a = Math.round(Math.min(1, baseAlpha * (HERO_GLOW_ALPHA[level] ?? 1)) * 255)
    .toString(16)
    .padStart(2, "0");
  return `0 ${offY}px ${blur}px ${color}${a}`;
}

export default function InvitationView({ invOverride = null, previewMode = false }) {
  const { slug, token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fetched, setFetched] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [heroRatio, setHeroRatio] = useState(null);
  const scopeRef = useRef(null);
  const downloadPdfRef = useRef(null);
  // Measure the hero width so the overlaid texts scale uniformly across phone widths
  // (design baseline = 390px). This makes every device — and the editor preview — look identical.
  const [heroW, setHeroW] = useState(0);
  const heroRoRef = useRef(null);
  const setHeroFrame = useCallback((node) => {
    if (heroRoRef.current) { heroRoRef.current.disconnect(); heroRoRef.current = null; }
    if (node) {
      setHeroW(node.getBoundingClientRect().width);
      const ro = new ResizeObserver((entries) => {
        for (const e of entries) setHeroW(e.contentRect.width);
      });
      ro.observe(node);
      heroRoRef.current = ro;
    }
  }, []);
  const heroK = heroW ? heroW / 390 : 1;
  const inv = invOverride || fetched;
  const loading = invOverride ? false : loadingState;

  useEffect(() => {
    if (invOverride) return;
    (async () => {
      try {
        const data = token
          ? await getInvitationByPreviewToken(token)
          : await getInvitationBySlug(slug);
        setFetched(data);
      } catch (e) {
        if (token && e?.response?.status === 410) {
          setError(e?.response?.data?.detail || "El enlace de prueba ya no está disponible.");
        } else {
          setError("Invitación no encontrada");
        }
      } finally {
        setLoadingState(false);
      }
    })();
  }, [slug, token, invOverride]);

  const isPreviewLink = !!token && !invOverride;

  const orderedBlocks = useMemo(() => resolveBlockOrder(inv?.block_order), [inv?.block_order]);
  useThemeVars(previewMode ? null : inv?.custom_style);

  // Load any user-added Google Fonts (custom_style.custom_fonts) on mount / change
  useEffect(() => {
    (inv?.custom_style?.custom_fonts || []).forEach((f) => f && f.family && loadGoogleFont(f.family));
  }, [inv?.custom_style?.custom_fonts]);

  // Auto-trigger export when ?pdf=1 is present (used from the Editor "keepsake" panel)
  useEffect(() => {
    if (!inv || loading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("pdf") === "1") {
      const t = setTimeout(() => { downloadPdfRef.current && downloadPdfRef.current(); }, 800);
      return () => clearTimeout(t);
    }
  }, [inv, loading]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <Loader2 className="animate-spin text-warmgold" size={28} />
      </div>
    );
  }

  if (error || !inv) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream p-8 text-center">
        <div>
          <h1 className="headline text-4xl text-ink">Ups...</h1>
          <p className="text-inksoft mt-4">{error || "No pudimos cargar tu invitación."}</p>
          <Link to="/" className="btn-primary mt-8">Ir al inicio</Link>
        </div>
      </div>
    );
  }

  const theme = getTheme(inv.theme);
  const bg = resolveImageUrl(inv.generated_bg_url || inv.hero_image_url);
  const date = formatDate(inv.event_date);
  // Countdown targets the ceremony time when the host chose so (and a ceremony time exists); otherwise the party time
  const countdownTime = (inv.countdown_target === "ceremony" && inv.ceremony_enabled && inv.ceremony_time)
    ? inv.ceremony_time
    : inv.event_time;
  const styleVars = buildStyleVars(inv.custom_style);
  const globalBg = resolveImageUrl(inv.global_bg_url);
  const globalBgOpacity = typeof inv.global_bg_opacity === "number" ? inv.global_bg_opacity : 0.3;
  const isOwner = !previewMode && user && inv.owner_id === user.user_id;

  // Auto-deactivation: if auto_deactivate_at is set and current time >= it, treat as deactivated
  const autoDeactivated = (() => {
    if (!inv.auto_deactivate_at) return false;
    const t = new Date(inv.auto_deactivate_at);
    if (isNaN(t.getTime())) return false;
    return Date.now() >= t.getTime();
  })();
  const isDeactivated = inv.is_active === false || autoDeactivated;

  // Guest-facing block screen when the invitation is deactivated by the host (manual or auto).
  // Owner still sees the full invitation (so they can preview and re-enable).
  if (isDeactivated && !isOwner && !previewMode && !isPreviewLink) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream p-6 text-center">
        <div className="max-w-md" data-testid="invitation-disabled-screen">
          <div className="w-20 h-20 mx-auto rounded-full bg-warmgold/10 grid place-items-center border border-warmgold/30">
            <Lock size={28} className="text-warmgold" />
          </div>
          <h1 className="headline text-4xl md:text-5xl text-ink mt-8">Invitación desactivada</h1>
          <p className="text-inksoft mt-4 leading-relaxed">
            {autoDeactivated && inv.is_active !== false
              ? "Este evento ya finalizó y la invitación se cerró automáticamente."
              : `Esta invitación fue desactivada temporalmente por el anfitrión${inv.hosts ? ` (${inv.hosts})` : ""}.`}
            {" "}Por favor contáctalo directamente para más información.
          </p>
          <Link to="/" className="btn-ghost mt-8" data-testid="invitation-disabled-home-btn">
            Ir al inicio de Fernebia
          </Link>
        </div>
      </div>
    );
  }

  // Subtitle uses --fn-warmgold (accent_color). Compute a soft cloud-glow color OPPOSITE its luminance
  // so the text is always legible against the hero image.
  // Readability cloud behind the hero texts — the host can dial it down (0) so the photo shows through
  const glowFactor = Math.max(0, Math.min(1, inv.hero_glow_opacity ?? 1));
  const glowW = Math.max(0.01, Math.min(1.5, Number(inv.hero_glow_width ?? 1)));
  const glowH = Math.max(0.01, Math.min(1.5, Number(inv.hero_glow_height ?? 1)));
  const heroTextOffset = Number(inv.hero_text_offset || 0);
  const subtitleColor = (inv.custom_style && (inv.custom_style.subtitle_color || inv.custom_style.accent_color)) || "#C5A880";
  const subtitleStroke = Math.max(0, Math.min(6, Number((inv.custom_style && inv.custom_style.subtitle_stroke_width) || 0)));
  const subtitleStrokeColor = (inv.custom_style && inv.custom_style.subtitle_stroke_color) || "#1E1814";
  const subtitleIsDark = hexLuminance(subtitleColor) < 0.55;
  // "auto" picks the cloud that contrasts with the subtitle color; the host can force white, black or remove it
  const glowEnabled = inv.hero_glow_tone !== "none" && glowFactor > 0;
  const isCustomGlow = inv.hero_glow_tone === "custom";
  const customGlowColor = inv.hero_glow_color || "#C5A880";
  const glowTone = inv.hero_glow_tone === "light" || inv.hero_glow_tone === "dark"
    ? inv.hero_glow_tone
    : (subtitleIsDark ? "light" : "dark");
  const subtitleGlow = isCustomGlow
    ? hexToRgba(customGlowColor, Number((0.9 * glowFactor).toFixed(3)))
    : glowTone === "light"
    ? `rgba(253, 248, 240, ${(0.98 * glowFactor).toFixed(3)})`  // light warm cream cloud
    : `rgba(30, 24, 20, ${(0.75 * glowFactor).toFixed(3)})`;     // dark warm ink cloud
  const labelGlow = isCustomGlow
    ? hexToRgba(customGlowColor, Number((0.85 * glowFactor).toFixed(3)))
    : glowTone === "light"
    ? `rgba(253, 248, 240, ${(0.92 * glowFactor).toFixed(3)})`
    : `rgba(30, 24, 20, ${(0.7 * glowFactor).toFixed(3)})`;
  const THEME_LABEL_SIZES = { xs: "0.58rem", sm: "0.66rem", md: "0.78rem", lg: "0.92rem", xl: "1.1rem", xxl: "1.35rem" };
  const themeLabelSize = THEME_LABEL_SIZES[(inv.custom_style && inv.custom_style.theme_label_size) || ""] || null;
  const themeLabelStroke = Math.max(0, Math.min(4, Number((inv.custom_style && inv.custom_style.theme_label_stroke_width) || 0)));
  const themeLabelStrokeColor = (inv.custom_style && inv.custom_style.theme_label_stroke_color) || "#1E1814";

  // Dark color from the invitation palette — used for text outlines / strokes on the title
  const paletteDark = (inv.custom_style && inv.custom_style.text_color) || "#3E3A35";

  // Hero TITLE & HOSTS — configurable outline width/color + colored blur (difuminado) color/intensity
  const cs = inv.custom_style || {};
  const num = (v, d) => (v != null && v !== "" ? Number(v) : d);
  const titleStrokeW = Math.max(0, Math.min(10, num(cs.title_stroke_width, 3.5)));
  const titleStrokeColor = cs.title_stroke_color || paletteDark;
  const titleGlow = cs.title_glow || "medium";
  const titleGlowColor = cs.title_glow_color || paletteDark;
  const hostsStrokeW = Math.max(0, Math.min(10, num(cs.hosts_stroke_width, 3)));
  const hostsStrokeColor = cs.hosts_stroke_color || paletteDark;
  const hostsGlow = cs.hosts_glow || "medium";
  const hostsGlowColor = cs.hosts_glow_color || paletteDark;

  const shareWhatsApp = () => {
    const url = `${window.location.origin}/i/${inv.slug}`;
    const text = encodeURIComponent(`Te comparto nuestra invitación: ${inv.title}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/i/${inv.slug}`);
    toast.success("Link copiado");
  };

  // Build a Google Calendar "add event" URL from the invitation. Returns null if date/time missing.
  const buildCalendarUrl = () => {
    if (!inv.event_date || !inv.event_time) return null;
    const start = new Date(`${inv.event_date}T${inv.event_time}:00`);
    if (isNaN(start.getTime())) return null;
    const durationH = typeof inv.event_duration_hours === "number" && inv.event_duration_hours > 0 ? inv.event_duration_hours : 5;
    const end = new Date(start.getTime() + durationH * 60 * 60 * 1000);
    // Google Calendar expects UTC dates in YYYYMMDDTHHMMSSZ format
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: inv.title || "Evento",
      dates: `${fmt(start)}/${fmt(end)}`,
      details: inv.message || `Te esperamos en ${inv.venue_name || inv.address || ""}`,
      location: [inv.venue_name, inv.address].filter(Boolean).join(", "),
    });
    return `https://www.google.com/calendar/render?${params.toString()}`;
  };
  const addToCalendar = () => {
    const url = buildCalendarUrl();
    if (!url) { toast.error("Falta fecha u hora del evento"); return; }
    window.open(url, "_blank");
  };
  const goBack = () => {
    if (isOwner) navigate("/dashboard");
    else navigate("/");
  };

  const downloadPdf = async () => {
    if (!scopeRef.current || exportingPdf) return;
    try {
      setExportingPdf(true);
      toast.loading("Generando PDF…", { id: "pdf-export" });
      // Ensure all lazy content (below-the-fold blocks with whileInView) is rendered before capture
      window.scrollTo({ top: document.body.scrollHeight, behavior: "instant" });
      await new Promise((r) => setTimeout(r, 350));
      window.scrollTo({ top: 0, behavior: "instant" });
      await new Promise((r) => setTimeout(r, 200));
      const safe = (inv.title || "invitacion").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-|-$/g, "") || "invitacion";
      await exportElementAsPdf(scopeRef.current, `${safe}.pdf`);
      toast.success("PDF descargado", { id: "pdf-export" });
    } catch (e) {
      toast.error("No se pudo generar el PDF", { id: "pdf-export" });
    } finally {
      setExportingPdf(false);
    }
  };
  downloadPdfRef.current = downloadPdf;

  // ============ BLOCK RENDERERS ============
  const BLOCKS = {
    date_block: (
      <BlockShell key="date_block" className="py-14 px-6 text-center" testid="block-date_block">
        <p className="overline">Reserva la fecha</p>
        <div className="mt-6 inline-grid grid-cols-3 items-center gap-6 md:gap-10">
          <div className="text-right">
            <p className="text-xs tracking-widest text-inksoft uppercase">{date.weekday}</p>
            <p className="script text-warmgold text-3xl mt-1">{date.month}</p>
          </div>
          <div className="flex flex-col items-center">
            <p className="headline text-6xl md:text-7xl text-ink leading-none">{date.day}</p>
            {countdownTime && (
              <p className="script text-warmgold text-2xl mt-3 leading-none" data-testid="date-block-time">{formatTime12h(countdownTime)}</p>
            )}
          </div>
          <div className="text-left">
            <p className="text-xs tracking-widest text-inksoft uppercase">Año</p>
            <p className="script text-warmgold text-3xl mt-1" data-testid="date-block-year">{date.year}</p>
          </div>
        </div>
      </BlockShell>
    ),
    countdown: (
      <BlockShell key="countdown" className="px-6" testid="block-countdown">
        <Countdown theme={inv.theme} date={inv.event_date} time={countdownTime} label={inv.countdown_label || theme?.countdownLabel} />
      </BlockShell>
    ),
    hashtag: inv.hashtag ? (
      <section key="hashtag" className="py-6 px-6 text-center" data-testid="block-hashtag">
        <div className="inline-flex items-center gap-2 glass rounded-full px-5 py-2">
          <Hash size={14} className="text-warmgold" />
          <span className="script text-warmgold text-2xl">{inv.hashtag.replace(/^#/, "")}</span>
        </div>
      </section>
    ) : null,
    message: inv.message ? (
      <BlockShell key="message" className="py-14 px-8 text-center" testid="block-message">
        <div className="fn-message inline-block">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="headline italic text-2xl md:text-3xl text-ink leading-relaxed text-balance whitespace-pre-line"
            style={{ fontFamily: "var(--fn-message-font, var(--fn-headline, 'Playfair Display', serif))" }}
            data-testid="message-text"
          >
            {inv.message_show_quotes === false
              ? withBigEmojis(inv.message)
              : <>&ldquo;{withBigEmojis(inv.message)}&rdquo;</>}
          </motion.p>
        </div>
        {inv.hosts && (inv.signature_block || "message") === "message" && <p className="script text-warmgold text-2xl mt-6" data-testid="message-signature">— {inv.hosts}</p>}
      </BlockShell>
    ) : null,
    invitation_message: inv.invitation_message ? (
      <BlockShell key="invitation_message" className="py-14 px-8 text-center" testid="block-invitation_message">
        <div className="fn-message inline-block">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="headline italic text-2xl md:text-3xl text-ink leading-relaxed text-balance whitespace-pre-line"
            style={{ fontFamily: "var(--fn-message-font, var(--fn-headline, 'Playfair Display', serif))" }}
            data-testid="invitation-message-text"
          >
            {withBigEmojis(inv.invitation_message)}
          </motion.p>
        </div>
        {inv.hosts && inv.signature_block === "invitation_message" && <p className="script text-warmgold text-2xl mt-6" data-testid="invitation-message-signature">— {inv.hosts}</p>}
      </BlockShell>
    ) : null,
    venue: (inv.venue_name || inv.address || (inv.ceremony_enabled && (inv.ceremony_venue_name || inv.ceremony_address))) ? (
      <BlockShell key="venue" className="py-14 px-6 relative bg-sand/60" testid="block-venue">
        <div className="text-center">
          <p className="overline">Cuándo y dónde</p>
          <h3 className="headline text-3xl md:text-4xl text-ink mt-3">Nos vemos aquí</h3>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 max-w-md mx-auto">
          <div className="glass rounded-2xl p-5 text-center">
            <Calendar size={20} className="text-warmgold mx-auto" />
            <p className="text-xs uppercase tracking-widest text-inksoft mt-3">Fecha</p>
            <p className="fn-body-font text-base text-ink mt-1">{date.day} {date.month}</p>
          </div>
          <div className="glass rounded-2xl p-5 text-center">
            <Clock size={20} className="text-warmgold mx-auto" />
            <p className="text-xs uppercase tracking-widest text-inksoft mt-3">Hora</p>
            <p className="fn-body-font text-base text-ink mt-1">{formatTime12h(inv.ceremony_enabled && inv.ceremony_time ? inv.ceremony_time : inv.event_time)}</p>
          </div>
        </div>
        {inv.event_date && inv.event_time && (
          <div className="mt-4 max-w-md mx-auto flex justify-center">
            <button
              onClick={addToCalendar}
              data-testid="add-to-calendar-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-white/70 text-ink border border-warmgold/60 hover:bg-white transition-colors shadow-sm"
            >
              <CalendarPlus size={14} className="text-warmgold" /> Agregar a mi calendario
            </button>
          </div>
        )}
        {inv.ceremony_enabled && (inv.ceremony_venue_name || inv.ceremony_address) && (
          <VenueCard
            label={inv.ceremony_label || "Misa"}
            time={inv.ceremony_time}
            name={inv.ceremony_venue_name}
            address={inv.ceremony_address}
            gmapsUrl={inv.ceremony_gmaps_url}
            testid="ceremony"
          />
        )}
        {(inv.venue_name || inv.address) && (
          <VenueCard
            label={inv.ceremony_enabled ? "Fiesta" : ""}
            time={inv.ceremony_enabled ? inv.event_time : ""}
            name={inv.venue_name}
            address={inv.address}
            gmapsUrl={inv.gmaps_url}
            testid="venue"
          />
        )}
      </BlockShell>
    ) : null,
    itinerary: (
      <BlockShell key="itinerary" testid="block-itinerary">
        <Itinerary items={inv.itinerary} showTimes={inv.itinerary_show_times !== false} />
      </BlockShell>
    ),
    padrinos: (
      <BlockShell key="padrinos" testid="block-padrinos">
        <Padrinos padrinos={inv.padrinos} />
      </BlockShell>
    ),
    dresscode: (
      <BlockShell key="dresscode" testid="block-dresscode">
        <Dresscode dresscode={inv.dresscode} />
      </BlockShell>
    ),
    extras: (inv.lodging || inv.kids_policy || inv.parking_info || inv.live_stream_url || (inv.extra_fields || []).some((f) => f && (f.title || f.text))) ? (
      <BlockShell key="extras" className="py-14 px-6 bg-sand/40" testid="block-extras">
        <div className="text-center mb-8">
          <p className="overline">Info importante</p>
          <h3 className="headline text-3xl text-ink mt-2">Todo lo que necesitas saber</h3>
        </div>
        <div className="max-w-md mx-auto grid grid-cols-1 gap-4">
          {inv.lodging && <InfoCard icon={BedDouble} title="Hospedaje" text={inv.lodging} testid="info-lodging" />}
          {inv.kids_policy && <InfoCard icon={BabyIcon} title="Peques" text={inv.kids_policy} testid="info-kids" />}
          {inv.parking_info && <InfoCard icon={Car} title="Estacionamiento" text={inv.parking_info} testid="info-parking" />}
          {(inv.extra_fields || []).filter((f) => f && (f.title || f.text)).map((f, i) => (
            <InfoCard key={`extra-${i}`} icon={Info} title={f.title || "Info"} text={f.text} testid={`info-extra-${i}`} />
          ))}
          {inv.live_stream_url && (
            <a href={inv.live_stream_url} target="_blank" rel="noreferrer" className="glass rounded-2xl p-5 flex items-start gap-4 hover:-translate-y-1 transition-transform" data-testid="info-livestream">
              <Video size={20} className="text-warmgold mt-1" />
              <div>
                <p className="text-xs uppercase tracking-widest text-inksoft">Live streaming</p>
                <p className="text-ink mt-1">Únete a la transmisión en vivo →</p>
              </div>
            </a>
          )}
        </div>
      </BlockShell>
    ) : null,
    gallery: (inv.photo_urls || []).filter(Boolean).length > 0 ? (
      <section key="gallery" className="py-14 px-6" data-testid="block-gallery">
        <div className="text-center mb-8">
          <p className="overline">Nuestra historia</p>
          <h3 className="headline text-3xl text-ink mt-2">Momentos</h3>
        </div>
        <GalleryMasonry photos={inv.photo_urls.filter(Boolean)} onPhotoClick={(u) => setLightboxSrc(resolveImageUrl(u))} />
      </section>
    ) : null,
    gift: (inv.gift?.message || inv.gift?.bank_details || inv.gift?.registry_url) ? (
      <BlockShell key="gift" className="py-14 px-6 text-center bg-sand/40" testid="block-gift">
        <Gift size={22} className="text-warmgold mx-auto" />
        <p className="overline mt-4">Regalos</p>
        {inv.gift.sticker_url && (
          <img src={resolveImageUrl(inv.gift.sticker_url)} alt="" className="h-56 w-auto max-w-[80%] object-contain mx-auto mt-4 drop-shadow-md" data-testid="gift-sticker" />
        )}
        {inv.gift.message && <p className="text-inksoft mt-4 max-w-md mx-auto leading-relaxed italic">&ldquo;{inv.gift.message}&rdquo;</p>}
        <div className="mt-6 max-w-md mx-auto space-y-3">
          {inv.gift.bank_details && (
            <details className="glass rounded-2xl p-5 text-left">
              <summary className="cursor-pointer text-ink font-medium">Datos bancarios / Sobre</summary>
              <p className="text-inksoft text-sm mt-3 whitespace-pre-wrap">{inv.gift.bank_details}</p>
            </details>
          )}
          {inv.gift.registry_url && (
            giftRegistryHref(inv.gift.registry_url) ? (
              <a href={giftRegistryHref(inv.gift.registry_url)} target="_blank" rel="noreferrer" className="btn-primary" data-testid="gift-registry-btn">
                Ver mesa de regalos
              </a>
            ) : (
              <button type="button" onClick={() => setGiftModalOpen(true)} className="btn-primary" data-testid="gift-registry-btn">
                Ver mesa de regalos
              </button>
            )
          )}
        </div>
      </BlockShell>
    ) : null,
    playlist: <PlaylistCollaborative key="playlist" invitationId={inv.id} enabled={inv.playlist_enabled} preview={previewMode} />,
    guestbook: <Guestbook key="guestbook" invitationId={inv.id} enabled={inv.guestbook_enabled} preview={previewMode} title={inv.guestbook_title} />,
  };

  return (
    <div ref={scopeRef} className="fn-scope relative w-full max-w-[640px] mx-auto bg-cream overflow-hidden" style={styleVars} data-testid="invitation-view">
      {/* GLOBAL BACKGROUND — fixed to viewport so it always fully covers regardless of scroll.
          Because it's position:fixed, it stays put while content scrolls over it (natural parallax effect) */}
      {globalBg && (
        <div className="fixed inset-0 pointer-events-none z-0" aria-hidden data-testid="global-bg-layer">
          <div
            className="absolute inset-0 bg-center bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${globalBg})`,
              opacity: globalBgOpacity,
              transform: "scale(1.05)",
            }}
          />
          {/* Soft cream veil so text remains legible */}
          <div className="absolute inset-0 bg-gradient-to-b from-cream/40 via-cream/20 to-cream/40" />
        </div>
      )}

      {/* Content lives above the parallax layer */}
      <div className="relative z-10">
      {isPreviewLink && (
        <div className="sticky top-0 z-50 bg-ink text-cream text-center text-xs md:text-sm py-2.5 px-4 shadow-md" data-testid="preview-banner">
          <span className="inline-flex items-center justify-center gap-2">
            <Info size={14} className="text-warmgold shrink-0" />
            Vista previa · este <strong>no es el enlace final</strong> de la invitación
          </span>
        </div>
      )}
      {/* Floating back button — ONLY visible to the owner (editor preview session) */}
      {isOwner && (
        <button
          onClick={goBack}
          className="fixed top-5 left-5 z-50 glass rounded-full w-11 h-11 grid place-items-center shadow-lg hover:scale-105 transition-transform"
          data-testid="invite-back-btn"
          aria-label="Volver"
        >
          <ArrowLeft size={18} className="text-ink" />
        </button>
      )}

      {/* Owner-only floating edit shortcut */}
      {isOwner && (
        <Link
          to={`/editor/${inv.id}`}
          className="fixed top-5 right-5 z-50 glass rounded-full px-4 py-2.5 flex items-center gap-2 shadow-lg hover:scale-105 transition-transform"
          data-testid="invite-edit-btn"
        >
          <Edit size={14} className="text-warmgold" />
          <span className="text-xs uppercase tracking-widest text-ink">Editar</span>
        </Link>
      )}

      {/* Owner-only floating "download as PDF" shortcut */}
      {isOwner && (
        <button
          onClick={downloadPdf}
          disabled={exportingPdf}
          className="fixed top-20 right-5 z-50 glass rounded-full px-4 py-2.5 flex items-center gap-2 shadow-lg hover:scale-105 transition-transform disabled:opacity-70"
          data-testid="pdf-export-btn"
          aria-label="Descargar como PDF"
        >
          {exportingPdf ? <Loader2 size={14} className="animate-spin text-warmgold" /> : <FileDown size={14} className="text-warmgold" />}
          <span className="text-xs uppercase tracking-widest text-ink">PDF</span>
        </button>
      )}

      {/* Owner-only warning banner when the invitation is deactivated (manual or auto) */}
      {isOwner && isDeactivated && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-warmgold/95 text-ink text-center py-2 px-4 text-xs md:text-sm font-medium flex items-center justify-center gap-2 shadow-lg" data-testid="owner-disabled-banner">
          <Lock size={14} />
          <span>
            Vista previa —{" "}
            {autoDeactivated && inv.is_active !== false
              ? <>Invitación <strong>desactivada automáticamente</strong> (fecha límite cumplida)</>
              : <>Esta invitación está <strong>desactivada</strong> para tus invitados</>}
          </span>
        </div>
      )}

      {/* HERO — the image is NEVER cropped. The wrapper takes the image's exact aspect ratio so the
          overlaid texts always stay inside the image, on phones and on desktop. */}
      <section className="relative overflow-hidden bg-cream flex justify-center">
        <div
          ref={setHeroFrame}
          className="relative w-full mx-auto"
          style={heroRatio ? { aspectRatio: String(heroRatio) } : { minHeight: "60vh" }}
          data-testid="hero-frame"
        >
        {bg && (
          <img
            src={bg}
            alt={inv.title}
            onLoad={(e) => {
              const { naturalWidth: w, naturalHeight: h } = e.target;
              if (w && h) setHeroRatio(w / h);
            }}
            className="w-full h-full object-contain block"
            data-testid="hero-image"
          />
        )}
        <div
          className="absolute top-0 pt-8 text-center"
          style={{ width: 390, left: "50%", marginLeft: -195, transform: `scale(${heroK})`, transformOrigin: "center top" }}
        >
          {inv.show_theme_label !== false && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} data-testid="hero-theme-label">
            <div className="relative inline-block">
              {/* Cloud behind the tagline — same tone/intensity controls as the subtitle */}
              {glowEnabled && (
              <div
                aria-hidden
                className="absolute pointer-events-none"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: `max(5px, calc((100% + 4rem) * ${glowW.toFixed(3)}))`,
                  height: `max(5px, calc((100% + 2.5rem) * ${glowH.toFixed(3)}))`,
                  background: labelGlow,
                  borderRadius: "9999px",
                  filter: "blur(22px)",
                  zIndex: 0,
                }}
              />
              )}
              <p
                className="relative overline font-semibold"
                style={{
                  zIndex: 1,
                  color: (inv.custom_style && inv.custom_style.theme_label_color) || paletteDark,
                  letterSpacing: "0.35em",
                  fontFamily: "var(--fn-theme-label-font, var(--fn-message-font, var(--fn-body, 'Outfit', sans-serif)))",
                  ...(themeLabelStroke > 0
                    ? {
                        WebkitTextStrokeWidth: `${themeLabelStroke}px`,
                        WebkitTextStrokeColor: themeLabelStrokeColor,
                        paintOrder: "stroke fill",
                      }
                    : {}),
                  fontSize: "calc(0.72rem * var(--fn-theme-label-scale, 1))",
                }}
                data-testid="hero-theme-label-text"
              >
                {inv.theme_label || theme?.tagline}
              </p>
            </div>
          </motion.div>
          )}
          {inv.show_invite_badge !== false && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-5 inline-block glass rounded-2xl px-5 py-3 text-center"
            data-testid="hero-invite-badge"
          >
            <p className="text-[9px] uppercase tracking-[0.3em] text-warmgold">Invitación</p>
            <p className="script text-ink text-xl -mt-1">Con cariño para ti</p>
          </motion.div>
          )}
        </div>
        <div
          className="absolute text-center px-6"
          style={{ width: 390, left: "50%", marginLeft: -195, bottom: `${(40 + heroTextOffset) * heroK}px`, transform: `scale(${heroK})`, transformOrigin: "center bottom" }}
          data-testid="hero-texts"
        >
          <div style={{ transform: `translateY(${-Number(inv.hero_offset_subtitle || 0)}px)` }} data-testid="hero-subtitle-wrap">
            <div className="fn-subtitle inline-block relative max-w-full">
              {/* Solid semi-opaque cloud with heavy blur — softens its own edges only, does NOT blur the hero image behind */}
              {glowEnabled && (
              <div
                aria-hidden
                className="absolute pointer-events-none"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: `max(5px, calc((100% + 5rem) * ${glowW.toFixed(3)}))`,
                  height: `max(5px, calc((100% + 3rem) * ${glowH.toFixed(3)}))`,
                  background: subtitleGlow,
                  borderRadius: "50%",
                  filter: "blur(24px)",
                  zIndex: 0,
                }}
              />
              )}
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 1 }}
                className="relative script text-warmgold"
                style={{
                  zIndex: 1,
                  fontSize: "1em",
                  lineHeight: 1.12,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  overflow: "hidden",
                  paddingBottom: "0.08em",
                  paddingLeft: "0.3em",
                  paddingRight: "0.3em",
                  color: "var(--fn-subtitle-color, var(--fn-warmgold, rgb(var(--c-warmgold))))",
                  ...(subtitleStroke > 0
                    ? {
                        WebkitTextStrokeWidth: `${subtitleStroke}px`,
                        WebkitTextStrokeColor: subtitleStrokeColor,
                        paintOrder: "stroke fill",
                      }
                    : {}),
                }}
              >
                {inv.subtitle || theme?.heroCopy}
              </motion.h1>
            </div>
          </div>
          <div style={{ transform: `translateY(${-Number(inv.hero_offset_title || 0)}px)` }} data-testid="hero-title-wrap">
            <div className="fn-title inline-block max-w-full">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 1 }}
                className="headline text-white mt-6 leading-tight text-balance font-bold"
                style={{
                  fontSize: "1em",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  overflow: "hidden",
                  paddingBottom: "0.08em",
                  paddingLeft: "0.3em",
                  paddingRight: "0.3em",
                  ...(titleStrokeW > 0
                    ? {
                        WebkitTextStrokeWidth: `${titleStrokeW}px`,
                        WebkitTextStrokeColor: titleStrokeColor,
                        paintOrder: "stroke fill",
                      }
                    : {}),
                  textShadow: heroGlowShadow(titleGlow, titleGlowColor, 4, 24, 0.333),
                }}
              >
                {inv.title}
              </motion.h2>
            </div>
          </div>
          {inv.hosts && (
            <div style={{ transform: `translateY(${-Number(inv.hero_offset_hosts || 0)}px)` }} data-testid="hero-hosts-wrap">
              <div className="fn-hosts inline-block">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                  className="text-white mt-4 tracking-widest text-base uppercase font-bold"
                  style={{
                    fontFamily: "var(--fn-hosts-font, var(--fn-body, 'Outfit', sans-serif))",
                    ...(hostsStrokeW > 0
                      ? {
                          WebkitTextStrokeWidth: `${hostsStrokeW}px`,
                          WebkitTextStrokeColor: hostsStrokeColor,
                          paintOrder: "stroke fill",
                        }
                      : {}),
                    textShadow: heroGlowShadow(hostsGlow, hostsGlowColor, 2, 12, 0.4),
                  }}
                >
                  {inv.hosts}
                </motion.p>
              </div>
            </div>
          )}
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-4 left-1/2"
          style={{ x: "-50%" }}
          data-testid="hero-scroll-arrow"
        >
          <span style={{ position: "relative", display: "inline-flex" }}>
            {hostsStrokeW > 0 && (
              <ChevronDown
                size={32}
                strokeWidth={2.5 + hostsStrokeW * 2}
                style={{ position: "absolute", top: 0, left: 0, color: hostsStrokeColor }}
                aria-hidden
              />
            )}
            <ChevronDown size={32} strokeWidth={2.5} style={{ position: "relative", color: "#FFFFFF" }} />
          </span>
        </motion.div>
        </div>
      </section>

      {/* REORDERABLE BLOCKS — filtered by hidden_blocks */}
      {orderedBlocks
        .filter((id) => !(inv.hidden_blocks || []).includes(id))
        .map((id) => BLOCKS[id])
        .filter(Boolean)}

      {/* PD (postdata) — extra note, right before the RSVP */}
      {inv.postdata && (
        <section className="pb-4 pt-10 px-6" data-testid="block-postdata">
          <div className="max-w-md mx-auto glass rounded-3xl px-6 py-6 border border-warmgold/25">
            <p className="script text-warmgold text-3xl leading-none">P.D.</p>
            <p className="text-ink text-sm mt-2 leading-relaxed whitespace-pre-line" data-testid="postdata-text">{withBigEmojis(inv.postdata)}</p>
          </div>
        </section>
      )}

      {/* RSVP — always last, fixed */}
      <section className="py-16 px-6" id="rsvp" data-testid="block-rsvp">
        <div className="text-center mb-8">
          <p className="overline">Confirmación</p>
          <h3 className="headline text-4xl text-ink mt-3">¿Nos acompañas?</h3>
          <p className="text-inksoft mt-4 max-w-sm mx-auto">Nos encantaría saber que estarás con nosotros para preparar cada detalle con cariño.</p>
        </div>
        <RSVPForm invitation={inv} preview={previewMode || isPreviewLink} />
      </section>

      {/* SHARE + FOOTER — locked typography/color: does NOT inherit user's custom fonts or accent color */}
      <section
        className="py-14 px-6 text-center bg-ink text-cream"
        style={{ fontFamily: "'Outfit', ui-sans-serif, system-ui, sans-serif" }}
      >
        <p className="text-4xl text-cream font-light tracking-wide">Gracias</p>
        <p className="mt-3 max-w-sm mx-auto text-cream/80 text-sm">por ser parte de este día tan especial{inv.share_enabled !== false ? ". Comparte esta invitación con quien quieras." : "."}</p>
        {inv.share_enabled !== false && (
          <div className="mt-8 flex flex-col items-center justify-center gap-3">
            <button onClick={shareWhatsApp} className="btn-whatsapp" data-testid="share-whatsapp-btn">
              Compartir por WhatsApp
            </button>
            <button
              onClick={copyLink}
              data-testid="share-copy-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium bg-white/10 text-cream border border-warmgold/50 hover:bg-white/20 transition-colors"
            >
              <Copy size={14} /> Copiar link
            </button>
          </div>
        )}
        <p className="mt-10 text-xs uppercase tracking-[0.3em] text-cream/70">
          powered by <span className="text-cream text-sm normal-case tracking-normal font-medium">Fernebia</span>
        </p>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/privacidad.pdf`}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-3 text-[10px] uppercase tracking-[0.2em] text-cream/60 hover:text-cream underline underline-offset-4 transition-colors"
          data-testid="privacy-link"
        >
          Aviso de privacidad y términos
        </a>
      </section>
      </div>
      {/* MusicPlayer rendered OUTSIDE fn-scope container so no ancestor stacking context can hide it */}
      <MusicPlayer music={inv.music} />

      {/* Photo lightbox — fullscreen popup to contemplate a single photo */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxSrc(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white grid place-items-center transition-colors"
            aria-label="Cerrar"
            data-testid="lightbox-close"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
          <img
            src={lightboxSrc}
            alt="Foto ampliada"
            className="max-w-full max-h-[92vh] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Gift registry MESSAGE page — shown when the registry value is a note, not a URL */}
      {giftModalOpen && (
        <div
          className="fixed inset-0 z-[9999] backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-200"
          style={{ backgroundColor: "rgba(28,24,22,0.94)" }}
          onClick={() => setGiftModalOpen(false)}
          data-testid="gift-message-modal"
        >
          <button
            type="button"
            onClick={() => setGiftModalOpen(false)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white grid place-items-center transition-colors"
            aria-label="Cerrar"
            data-testid="gift-message-close"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
          <div className="max-w-md text-center" onClick={(e) => e.stopPropagation()}>
            <Gift size={34} className="text-warmgold mx-auto" />
            <p className="overline mt-4" style={{ color: "rgba(255,255,255,0.75)" }}>Mesa de regalos</p>
            <p
              className="script text-3xl md:text-4xl mt-5 leading-relaxed whitespace-pre-line"
              style={{ color: "#F5EFE6" }}
              data-testid="gift-message-text"
            >
              {inv.gift.registry_url}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryMasonry({ photos, onPhotoClick }) {
  // Measure each photo's aspect ratio so we can greedily pack them into the
  // SHORTEST column (true Pinterest-style masonry) — no big gaps on mobile or desktop.
  const [ratios, setRatios] = useState({});
  useEffect(() => {
    let active = true;
    photos.forEach((u) => {
      const img = new window.Image();
      img.onload = () => {
        if (active && img.naturalWidth && img.naturalHeight) {
          setRatios((r) => (r[u] ? r : { ...r, [u]: img.naturalWidth / img.naturalHeight }));
        }
      };
      img.src = resolveImageUrl(u);
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.join("|")]);

  const cols = [[], []];
  const heights = [0, 0];
  photos.forEach((u, i) => {
    const ratio = ratios[u] || 1; // assume square until measured
    const h = 1 / ratio; // relative height for a fixed column width
    const target = heights[0] <= heights[1] ? 0 : 1;
    cols[target].push({ u, i });
    heights[target] += h + 0.08; // include the gap so columns stay balanced
  });

  return (
    <div className="flex gap-3 max-w-md mx-auto items-start" data-testid="gallery-masonry">
      {cols.map((col, ci) => (
        <div key={ci} className="flex-1 min-w-0 flex flex-col gap-3">
          {col.map(({ u, i }) => (
            <motion.button
              type="button"
              key={u || i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              onClick={() => onPhotoClick(u)}
              className="w-full block rounded-2xl overflow-hidden shadow-md bg-cream focus:outline-none focus:ring-2 focus:ring-warmgold/60"
              data-testid={`gallery-photo-${i}`}
              aria-label={`Ver foto ${i + 1}`}
            >
              <img src={resolveImageUrl(u)} alt={`Foto ${i}`} className="w-full h-auto block" />
            </motion.button>
          ))}
        </div>
      ))}
    </div>
  );
}

function VenueCard({ label, time, name, address, gmapsUrl, testid }) {
  const open = () => {
    if (gmapsUrl) window.open(gmapsUrl, "_blank");
    else if (address) window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, "_blank");
  };
  return (
    <div className="mt-6 max-w-md mx-auto glass rounded-2xl p-6 text-center" data-testid={`${testid}-card`}>
      {label && (
        <p className="text-[10px] uppercase tracking-[0.3em] text-warmgold">
          {label}{time ? ` · ${formatTime12h(time)}` : ""}
        </p>
      )}
      <MapPin size={20} className="text-warmgold mx-auto mt-3" />
      <p className="fn-body-font text-xl md:text-2xl font-medium text-ink mt-3 leading-snug">{name}</p>
      <p className="text-inksoft text-base mt-2 leading-relaxed">{address}</p>
      {address && (
        <div className="mt-5 rounded-xl overflow-hidden aspect-video border border-warmgold/20" data-testid={`${testid}-gmaps-embed`}>
          <iframe
            title={`google-map-${testid}`}
            src={`https://maps.google.com/maps?q=${encodeURIComponent(address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
            width="100%"
            height="100%"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0, display: "block" }}
          />
        </div>
      )}
      {(gmapsUrl || address) && (
        <button onClick={open} className="btn-primary mt-5 text-sm py-2.5 px-5" data-testid={`${testid === "venue" ? "open-map-btn" : `open-map-${testid}-btn`}`}>
          <MapPin size={14} /> Ver ubicación en Google Maps
        </button>
      )}
    </div>
  );
}

function InfoCard({ icon: Icon, title, text, testid }) {
  return (
    <div className="glass rounded-2xl p-5 flex items-start gap-4" data-testid={testid}>
      <Icon size={20} className="text-warmgold mt-1 shrink-0" />
      <div>
        <p className="text-xs uppercase tracking-widest text-inksoft">{title}</p>
        <p className="text-ink text-sm mt-1 leading-relaxed whitespace-pre-line">{withBigEmojis(text)}</p>
      </div>
    </div>
  );
}
